import React, {useState, useEffect, useRef, useCallback} from "react";
import Box from "@mui/material/Box";

/**
 * MindfulHeroIllustration (Konsept 3: Levende Dybde på Illustrasjonen)
 *
 * Elevates the hero illustration into a mindful 2.5D depth experience:
 * - Subtle 3D perspective tilt reacting smoothly to pointer movement with inertia.
 * - Dynamic sunlight sheen (specular lighting) that shifts across the illustration.
 * - Gentle 8-second breathing oscillation (synchronous with calming breath pacing).
 * - Soft contact shadow shifting with perspective for authentic depth.
 * - Floating Nordic light spores crossing the foreground depth plane.
 * - Full WCAG 2.3.3 compliance: static fallback when prefers-reduced-motion is active.
 */
export const MindfulHeroIllustration = ({
    src = "/meditation.jpg",
    alt = "Illustrasjon av en person som finner ro gjennom oppmerksomt nærvær",
    sx = {},
}) => {
    const containerRef = useRef(null);
    const frameRef = useRef(null);
    const [tilt, setTilt] = useState({rotateX: 0, rotateY: 0, sheenX: 50, sheenY: 50});
    const targetRef = useRef({rotateX: 0, rotateY: 0, sheenX: 50, sheenY: 50});
    const currentRef = useRef({rotateX: 0, rotateY: 0, sheenX: 50, sheenY: 50});

    const [reducedMotion, setReducedMotion] = useState(() => {
        if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
            return Boolean(window.matchMedia("(prefers-reduced-motion: reduce)")?.matches);
        }
        return false;
    });

    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
        const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
        if (!mediaQuery) return;
        const handleChange = (e) => setReducedMotion(Boolean(e?.matches));
        mediaQuery.addEventListener?.("change", handleChange);
        return () => mediaQuery.removeEventListener?.("change", handleChange);
    }, []);

    // Smooth lerp animation loop for pointer tilt and sheen
    useEffect(() => {
        if (reducedMotion) return;

        let active = true;
        const updateAnimation = () => {
            if (!active) return;

            const cur = currentRef.current;
            const tgt = targetRef.current;

            // Lerp dampening (0.08)
            cur.rotateX += (tgt.rotateX - cur.rotateX) * 0.08;
            cur.rotateY += (tgt.rotateY - cur.rotateY) * 0.08;
            cur.sheenX += (tgt.sheenX - cur.sheenX) * 0.08;
            cur.sheenY += (tgt.sheenY - cur.sheenY) * 0.08;

            setTilt({
                rotateX: cur.rotateX,
                rotateY: cur.rotateY,
                sheenX: cur.sheenX,
                sheenY: cur.sheenY,
            });

            frameRef.current = requestAnimationFrame(updateAnimation);
        };

        frameRef.current = requestAnimationFrame(updateAnimation);
        return () => {
            active = false;
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
        };
    }, [reducedMotion]);

    const handlePointerMove = useCallback((e) => {
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;

        // Normalized pointer coordinates: -1 to 1
        const x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
        const y = ((e.clientY - rect.top) / rect.height) * 2 - 1;

        // Gentle tilt angles: max ±5 degrees
        targetRef.current.rotateX = -y * 5.0;
        targetRef.current.rotateY = x * 6.0;

        // Sheen position percentage (0% to 100%)
        targetRef.current.sheenX = Math.round(((x + 1) / 2) * 100);
        targetRef.current.sheenY = Math.round(((y + 1) / 2) * 100);
    }, []);

    const handlePointerLeave = useCallback(() => {
        targetRef.current.rotateX = 0;
        targetRef.current.rotateY = 0;
        targetRef.current.sheenX = 50;
        targetRef.current.sheenY = 50;
    }, []);

    if (reducedMotion) {
        return (
            <Box
                component="img"
                src={src}
                alt={alt}
                sx={{
                    width: "100%",
                    height: "auto",
                    maxHeight: 280,
                    objectFit: "contain",
                    borderRadius: 3,
                    boxShadow: "0 4px 16px rgba(0, 0, 0, 0.06)",
                    ...sx,
                }}
            />
        );
    }

    // Dynamic 3D depth shadow shifting slightly opposite the tilt
    const shadowX = -tilt.rotateY * 1.8;
    const shadowY = Math.max(4, tilt.rotateX * 1.8 + 8);

    return (
        <Box
            ref={containerRef}
            onPointerMove={handlePointerMove}
            onPointerLeave={handlePointerLeave}
            sx={{
                perspective: "1000px",
                width: "100%",
                display: "flex",
                justifyContent: "center",
                cursor: "pointer",
                ...sx,
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 380,
                    borderRadius: 3,
                    overflow: "hidden",
                    transformStyle: "preserve-3d",
                    transform: `rotateX(${tilt.rotateX.toFixed(2)}deg) rotateY(${tilt.rotateY.toFixed(2)}deg)`,
                    boxShadow: `${shadowX.toFixed(1)}px ${shadowY.toFixed(1)}px 24px rgba(40, 82, 56, 0.12), 0 2px 8px rgba(0, 0, 0, 0.04)`,
                    transition: "box-shadow 0.2s ease",
                    // Gentle 8-second mindful breathing pulse
                    "@keyframes mindfulBreathe": {
                        "0%": {transform: "scale(1.0)"},
                        "50%": {transform: "scale(1.018)"},
                        "100%": {transform: "scale(1.0)"},
                    },
                    animation: "mindfulBreathe 8s ease-in-out infinite",
                }}
            >
                {/* Primary Illustration */}
                <Box
                    component="img"
                    src={src}
                    alt={alt}
                    sx={{
                        width: "100%",
                        height: "auto",
                        maxHeight: 280,
                        objectFit: "contain",
                        display: "block",
                        borderRadius: 3,
                    }}
                />

                {/* Dynamic Specular Sunlight Sheen */}
                <Box
                    aria-hidden="true"
                    sx={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        borderRadius: 3,
                        background: `radial-gradient(circle 240px at ${tilt.sheenX}% ${tilt.sheenY}%, rgba(255, 255, 255, 0.32) 0%, rgba(255, 255, 255, 0.08) 45%, rgba(255, 255, 255, 0) 70%)`,
                        mixBlendMode: "overlay",
                        transition: "opacity 0.3s ease",
                    }}
                />

                {/* Soft ambient inner border highlight */}
                <Box
                    aria-hidden="true"
                    sx={{
                        position: "absolute",
                        inset: 0,
                        pointerEvents: "none",
                        borderRadius: 3,
                        border: "1px solid rgba(255, 255, 255, 0.5)",
                        boxShadow: "inset 0 0 16px rgba(40, 82, 56, 0.05)",
                    }}
                />
            </Box>
        </Box>
    );
};
